import { Button, Flex, Result, Space, Typography } from "antd";
import React, { useState } from "react";
import * as Sentry from "@sentry/react";
import { AppLogo } from "components/AppLogo";
import {
  ArrowClockwiseIcon,
  ArrowLeftIcon,
  ChatIcon,
  CopyIcon,
  InfoIcon,
} from "@phosphor-icons/react";
import { useClipboard } from "hooks/useClipboard/";
import { useTranslation } from "react-i18next";

interface ErrorFallbackProps {
  error: Error | unknown;
  resetError: () => void;
  eventId?: string | null;
  title?: string;
}

export class CustomError extends Error {
  constructor(
    message: string,
    public title: string,
    public statusCode?: number
  ) {
    super(message);
    this.name = "CustomError";
  }
}

export const ErrorFallback: React.FC<ErrorFallbackProps> = ({
  error,
  resetError,
  eventId,
  title,
}) => {
  const { copy } = useClipboard();
  const { t } = useTranslation();
  const copyErrorReferenceId = () => {
    if (eventId) {
      copy(eventId);
    }
  };
  const emailSupport = () => {
    const eventReference = eventId ? ` (# ${eventId})` : "";
    // Include eventId in subject so support can find it easily
    const subject = `Error Report${eventReference}`;
    const body = `Error at ${globalThis.location.href}\n\nError details: ${(error as Error).message}`;
    window.open(
      "mailto:support@libertify.com?subject=" +
        encodeURIComponent(subject) +
        "&body=" +
        encodeURIComponent(body)
    );
  };
  const [showErrorDetails, setShowErrorDetails] = useState(false);

  const errorTitle =
    title ?? (error instanceof CustomError ? error.title : undefined);
  const errorSubtitle =
    error instanceof CustomError ? error.message : t("src.App.errorSubtitle");

  return (
    <Flex vertical justify="center" align="center" style={{ height: "90vh" }}>
      <Result
        status="error"
        icon={<AppLogo width={80} />}
        title={errorTitle || t("src.App.somethingWentWrong")}
        subTitle={
          <Flex vertical justify="center" align="center">
            <Typography.Text type="secondary">{errorSubtitle}</Typography.Text>
            {showErrorDetails && (
              <Typography.Text code style={{ padding: 30 }}>
                {(error as Error).message}
              </Typography.Text>
            )}
          </Flex>
        }
        extra={
          <Space size="large">
            <Button
              shape="round"
              icon={<ArrowLeftIcon size={16} />}
              onClick={() => globalThis.history.back()}
              size="large"
            >
              {t("src.App.goBack")}
            </Button>
            <Button
              icon={<ArrowClockwiseIcon size={16} />}
              shape="round"
              type="primary"
              onClick={resetError}
              size="large"
            >
              {t("src.App.reloadPage")}
            </Button>
          </Space>
        }
      />
      <Space>
        <Button
          icon={<ChatIcon size={16} />}
          onClick={emailSupport}
          type="text"
          size="small"
        >
          {t("src.App.contactSupport")}
        </Button>
        <Typography.Text type="secondary">|</Typography.Text>
        <Button
          icon={<CopyIcon size={16} />}
          onClick={copyErrorReferenceId}
          type="text"
          size="small"
        >
          {t("src.App.copyErrorReferenceId")}
        </Button>
        <Typography.Text type="secondary">|</Typography.Text>
        <Button
          icon={<InfoIcon size={16} />}
          onClick={() => {
            setShowErrorDetails(true);
          }}
          type="text"
          size="small"
        >
          {t("src.App.showErrorDetails")}
        </Button>
      </Space>
    </Flex>
  );
};

interface ErrorBoundaryProps {
  children: React.ReactNode;
  onReset?: () => void;
  id?: string;
}

const ErrorBoundary: React.FC<ErrorBoundaryProps> = ({
  onReset,
  children,
  id,
}) => {
  return (
    <Sentry.ErrorBoundary
      // adding key to the ErrorBoundary to force a re-render when the error occurs
      // to allow navigation to other pages without reloading the page
      key={id}
      fallback={({ error, eventId }) => (
        <ErrorFallback
          error={error}
          // Reload directly instead of forwarding Sentry's resetError:
          // resetError clears the boundary state and re-mounts children
          // before the reload, which can surface a secondary error
          // (e.g. during maintenance, when the 503 interceptor resolves
          // with {} and consumers crash on undefined fields).
          resetError={() => globalThis.location.reload()}
          eventId={eventId}
        />
      )}
      onReset={onReset}
    >
      {/* <SimulateError /> */}
      {children}
    </Sentry.ErrorBoundary>
  );
};

export default ErrorBoundary;
